import React, { useState, useEffect, useRef } from 'react';

interface LazyImageProps {
  src: string;
  alt: string;
  style?: React.CSSProperties;
  className?: string;
  priority?: boolean; // Load immediately for above-fold images
  onError?: () => void;
}

const LazyImage: React.FC<LazyImageProps> = React.memo(({ 
  src, alt, style, className, priority = false, onError 
}) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const hasTriggered = useRef(false);

  useEffect(() => {
    const img = imgRef.current;
    if (!img || error || hasTriggered.current) return;

    // Priority images load immediately (logo, critical assets)
    if (priority) {
      img.src = src;
      hasTriggered.current = true;
      return;
    }

    // Fallback for browsers without IntersectionObserver
    if (!('IntersectionObserver' in window)) {
      img.src = src;
      hasTriggered.current = true;
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Load if visible OR if element is above viewport (negative top)
          if ((entry.isIntersecting || entry.boundingClientRect.top < 0) && !hasTriggered.current) {
            hasTriggered.current = true;
            img.src = src;
            observer.disconnect();
          }
        });
      },
      { rootMargin: '100px', threshold: 0 }
    );

    observer.observe(img);
    return () => observer.disconnect();
  }, [src, error, priority]);

  if (error) {
    return (
      <div style={{
        ...style as any,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(27, 90, 64, 0.05)',
        borderRadius: '8px'
      }}>
        <span style={{ fontSize: '24px', color: '#1B5A40' }}>✦</span>
      </div>
    );
  }

  return (
    <img
      ref={imgRef}
      alt={alt}
      className={className}
      style={{
        ...style,
        opacity: loaded ? 1 : 0,
        transition: 'opacity 0.3s ease',
      }}
      onLoad={() => setLoaded(true)}
      onError={() => { setError(true); onError?.(); }}
      loading={priority ? 'eager' : 'lazy'}
    />
  );
});

LazyImage.displayName = 'LazyImage';

export default LazyImage;
